<template>
  <div class="title">
    <h2>訂閱電子報</h2>
  </div>
  <table>
    <tr>
      <td class="title">訂閱電子報<span>（必填）</span></td>
      <td class="content">
        <input
          type="radio"
          id="contactChoice1"
          name="contact"
          :value="true"
          required
        />
        <label for="contactChoice1">訂閱</label>
        <input
          type="radio"
          id="contactChoice2"
          name="contact"
          :value="false"
          required
        />
        <label for="contactChoice2">取消訂閱</label>
      </td>
    </tr>
    <tr>
      <td class="title">電子報類別<span>（必填）</span></td>
      <td class="content">
        <a-checkbox-group v-model:value="subscriptionState.subscriptionType">
          <div v-for="{ label, value } in option.subscription" :key="value">
            <a-checkbox :value="value">{{ label }}</a-checkbox>
            <a-radio-group
              v-if="value === isEmploymentType && subscriptEmploymentType"
              v-model:value="subscriptionState.frequency"
              name="subscriptionFrequency"
              :options="option.frequency"
              required
            />
          </div>
        </a-checkbox-group>
      </td>
    </tr>
    <tr>
      <td class="title">
        <label for="email">電子信箱<span>（必填）</span></label>
      </td>
      <td class="content">
        <a-input
          id="email"
          title="電子信箱"
          v-model:value="subscriptionState.email"
          :style="{
            width: '200px',
          }"
          ref="emailInput"
          required
        ></a-input>
      </td>
    </tr>
    <tr>
      <td class="title">
        <label for="verify">驗證碼<span>（必填）</span></label>
      </td>
      <td class="content verify">
        <a-input
          id="verify"
          title="驗證碼"
          v-model:value="subscriptionState.verifyCode"
          :style="{ width: '100px' }"
          ref="verifyCodeInput"
          required
        ></a-input>
        <div class="verify_code">
          <div class="code" @click="refreshCode" value="驗證碼">
            <s-identify
              :identifyCode="identifyCode"
              title="驗證碼"
            ></s-identify>
          </div>
          <a-button class="refresh_btn" @click="refreshCode" type="button">
            <i class="fas fa-sync-alt"></i>變更驗證碼
          </a-button>
          <a-button
            @click="speak(identifyCode)"
            class="refresh_btn"
            type="button"
          >
            <i class="fas fa-play"></i>播放語音驗證碼
          </a-button>
        </div>
        <p class="verify_description">英文需區分大小寫</p>
      </td>
    </tr>
  </table>
  <div class="btns">
    <a-button
      type="primary"
      :style="{ padding: '4px 8px', marginRight: '8px' }"
      @click="submitForm"
      >完成送出</a-button
    >
    <a-button :style="{ padding: '4px 8px' }" @click="resetForm"
      >重新輸入</a-button
    >
  </div>
</template>

<script setup>
import {
  getSubscribableOptions,
  getSubscriptionFrequencyOptions,
  subscriptInfo,
} from "@/api/api";
import { reactive, ref, computed, onMounted } from "vue";
import SIdentify from "./sIdentify.vue";
import { message } from "ant-design-vue";
import { isEmail } from "@/func";

const emailInput = ref(null);
const verifyCodeInput = ref(null);

onMounted(() => {
  speechInit();
});

const speechInit = () => {
  if (typeof window !== "undefined" && window.speechSynthesis) {
    window.speechSynthesis.getVoices();
  }
};

const speak = (t) => {
  if (typeof window === "undefined" || !window.speechSynthesis) {
    message.info("裝置不支援語音播放，請改用畫面上的驗證碼");
    return;
  }

  const utterance = new SpeechSynthesisUtterance(
    `${t[0]} ${t[1]} ${t[2]} ${t[3]}`,
  );
  utterance.lang = "zh-TW";
  utterance.rate = 0.5;
  window.speechSynthesis.cancel();
  window.speechSynthesis.speak(utterance);
};
const isEmploymentType = "01";
const subscriptEmploymentType = computed(
  () => subscriptionState.subscriptionType.indexOf(isEmploymentType) > -1,
);
const option = reactive({
  subscription: [],
  frequency: [],
});

const fetchSubscriptionOptions = async () => {
  const { data } = await getSubscribableOptions();
  option.subscription = data.map(({ value, text }) => ({
    label: text,
    value,
  }));
};
const fetchFrequencyOptions = async () => {
  const { data } = await getSubscriptionFrequencyOptions();
  option.frequency = data.map(({ value, text }) => ({
    label: text,
    value,
  }));

  subscriptionState.frequency = option.frequency[0].value;
};

fetchSubscriptionOptions();
fetchFrequencyOptions();

const subscriptionState = reactive({
  subscription: true,
  subscriptionType: ["02"],
  frequency: "",
  email: "",
  verifyCode: "",
});

const submitForm = async () => {
  if (verify()) {
    const data = {
      mail: subscriptionState.email,
      subscriptionType: "02",
      frequency: subscriptEmploymentType.value
        ? subscriptionState.frequency
        : "",
      isSubscription: subscriptionState.subscription,
    };

    try {
      await subscriptInfo(data);
      message.success(
        subscriptionState.subscription ? "訂閱成功" : "已取消訂閱",
      );
      resetForm();
    } catch (error) {
      message.error(subscriptionState.subscription ? "訂閱失敗" : "取消失敗");
    }
  }
};

const verify = () => {
  if (dataIsEmpty()) {
    message.error("必填欄位不可為空");
    document.querySelector("input:invalid").focus();
    return false;
  }

  if (!isEmail(subscriptionState.email)) {
    emailInput.value.focus();
    message.error("請確認信箱格式");
    return false;
  }

  if (subscriptionState.verifyCode !== identifyCode.value) {
    verifyCodeInput.value.focus();
    message.error("驗證碼錯誤");
    refreshCode();
    return false;
  }

  return true;
};

const dataIsEmpty = () => {
  if (!subscriptionState.email || !subscriptionState.subscriptionType)
    return true;
  return false;
};

const resetForm = () => {
  subscriptionState.subscription = true;
  subscriptionState.subscriptionType = ["02"];
  subscriptionState.frequency = "";
  subscriptionState.email = "";
  subscriptionState.verifyCode = "";

  refreshCode();
};

const identifyCode = ref("");
const identifyCodes = ref("0123456789abcdwerwshdjeJKDHRJHKOOPLMKQ");
const randomNum = (min, max) => {
  max = max + 1;
  return Math.floor(Math.random() * (max - min) + min);
};
const makeCode = (data, len) => {
  for (let i = 0; i < len; i++) {
    identifyCode.value += data[randomNum(0, data.length - 1)];
  }
};
const refreshCode = () => {
  identifyCode.value = "";
  makeCode(identifyCodes.value, 4);
};
refreshCode();
</script>

<style lang="scss" scoped>
.title {
  margin-bottom: 12px;
  background: linear-gradient(
    to right,
    $main_sec_color 0 3%,
    $main_color 3% 100%
  );

  h2 {
    margin-left: 3%;
    padding: 20px;
    text-align: start;
    font-size: $h2;
    color: #fff;
  }
}

table {
  margin-bottom: 12px;
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  white-space: pre-wrap;
  word-wrap: break-word;
  font-size: $p;

  tr {
    &:first-child {
      td {
        border-top: 1px solid #d9d9d9;
      }
      td.title {
        &:first-child {
          border-radius: 4px 0 0 0;
        }
      }

      td.content {
        &:last-child {
          border-radius: 0 4px 0 0;
        }
      }
    }

    &:last-child {
      td.title {
        &:first-child {
          border-radius: 0 0 0 4px;
        }
      }
      td.content {
        &:last-child {
          border-radius: 0 0 4px 0;
        }
      }
    }

    td {
      padding: 8px;
      border-bottom: 1px solid #d9d9d9;
      text-align: start;
    }

    td.title {
      border-left: 1px solid #d9d9d9;
      width: 0;
      white-space: nowrap;
      background: #f5f5f5;

      span {
        color: red;
      }
    }

    td.content {
      border-right: 1px solid #d9d9d9;
    }
  }
}

.verify {
  display: flex;
  align-items: center;

  .verify_code {
    display: flex;
    align-items: center;
    .code {
      background-color: #eee;
      margin-left: 8px;
      border: 1px solid #d9d9d9;
      border-radius: 2px;
    }
    .refresh_btn {
      border: 1.5px solid #d9d9d9;
      border-radius: 2px;
      padding: 4px 8px;
      margin-left: 8px;
      background-color: #fff;
    }
    .ant-btn {
      height: auto;
      padding: 4px 16px;

      &:focus {
        outline: 2px solid black;
        outline-offset: 2px;
      }
    }
  }

  .verify_description {
    margin-left: 8px;
    color: red;
    font-size: 0.95em;
  }
}

.ant-btn:focus {
  outline: 2px solid black;
  outline-offset: 2px;
}
</style>
